/**
 * GanttFullRender — non-virtualised version of the Gantt used ONLY for PNG
 * export. Mounts off-screen, renders EVERY filtered row + the FULL timeline
 * width, lets html2canvas capture it, then unmounts.
 *
 * Trade-off: a 5k-row Gantt with full-year timeline is a tall, wide DOM tree
 * (~5MB image). We cap row rendering at 10000 to avoid browser OOM and warn
 * the user when truncation happens.
 */
import { diffDays, formatCell, getValue, parseDate } from "./GanttChart";

const ROW_HEIGHT = 28;
const COL_LABEL_H = 28;
const COL_FILTER_H = 0; // no filter row in the export
const HEADER_HEIGHT = COL_LABEL_H + COL_FILTER_H;
const MAX_EXPORT_ROWS = 10000;

export function GanttFullRender({ tasks, columns, syncMeta = {}, period: _period, zoom: _zoom, projectName: _projectName }) {
  const visibleCols = columns.filter((c) => c.visible).map((c) => ({ ...c, width: c.width || 120 }));
  const totalLeftWidth = visibleCols.reduce((s, c) => s + (c.width || 120), 0);

  const parsed = tasks.map((t) => ({ ...t, _start: parseDate(t.start), _finish: parseDate(t.finish) }));

  // Compute timeline bounds the same way GanttChart does
  let bounds = null;
  if (parsed.length) {
    const starts = parsed.map((t) => t._start).filter(Boolean);
    const finishes = parsed.map((t) => t._finish).filter(Boolean);
    if (starts.length && finishes.length) {
      const min = new Date(Math.min(...starts.map((d) => d.getTime())));
      const max = new Date(Math.max(...finishes.map((d) => d.getTime())));
      const totalDays = Math.max(7, diffDays(max, min) + 1);
      const colWidth = 18; // medium zoom for export
      bounds = { min, max, totalDays, colWidth, timelineWidth: totalDays * colWidth };
    }
  }

  const months = [];
  if (bounds) {
    const cur = new Date(bounds.min.getFullYear(), bounds.min.getMonth(), 1);
    while (cur <= bounds.max) {
      const ms = new Date(Math.max(cur.getTime(), bounds.min.getTime()));
      const me0 = new Date(cur.getFullYear(), cur.getMonth() + 1, 0);
      const me = me0 > bounds.max ? bounds.max : me0;
      months.push({
        key: `${cur.getFullYear()}-${cur.getMonth()}`,
        width: (diffDays(me, ms) + 1) * bounds.colWidth,
        label: cur.toLocaleDateString(undefined, { month: "short", year: "2-digit" }),
      });
      cur.setMonth(cur.getMonth() + 1);
      cur.setDate(1);
    }
  }

  const rows = parsed.slice(0, MAX_EXPORT_ROWS);
  const totalWidth = totalLeftWidth + (bounds?.timelineWidth || 0);
  const totalHeight = HEADER_HEIGHT + rows.length * ROW_HEIGHT;

  return (
    <div
      style={{ width: totalWidth, background: "#FFF", fontFamily: "Inter, system-ui, sans-serif" }}
      data-testid="gantt-full-export"
    >
      {/* Header */}
      <div style={{ display: "flex", height: HEADER_HEIGHT, background: "#FAFAFA", borderBottom: "1px solid #E4E4E7" }}>
        <div style={{ display: "flex", width: totalLeftWidth, borderRight: "1px solid #E4E4E7" }}>
          {visibleCols.map((c) => (
            <div key={c.key} style={{ width: c.width, padding: "0 8px", display: "flex", alignItems: "center", fontSize: 10, color: "#3F3F46", textTransform: "uppercase", letterSpacing: "0.05em" }}>
              {c.label}
            </div>
          ))}
        </div>
        {bounds && (
          <div style={{ display: "flex", width: bounds.timelineWidth }}>
            {months.map((m) => (
              <div key={m.key} style={{ width: m.width, borderRight: "1px solid #E4E4E7", padding: "0 8px", display: "flex", alignItems: "center", fontSize: 10, color: "#3F3F46", textTransform: "uppercase", letterSpacing: "0.05em" }}>{m.label}</div>
            ))}
          </div>
        )}
      </div>
      {/* Rows */}
      <div style={{ position: "relative" }}>
        {rows.map((t, idx) => (
          <div
            key={t.task_uid}
            style={{
              display: "flex",
              height: ROW_HEIGHT,
              borderBottom: "1px solid #F4F4F5",
              background: idx % 2 === 0 ? "#FFFFFF" : "#FAFAFA",
            }}
          >
            {/* Left cells */}
            <div style={{ display: "flex", width: totalLeftWidth, borderRight: "1px solid #E4E4E7" }}>
              {visibleCols.map((col, ci) => {
                const val = getValue(t, col.key);
                const indent = Math.max(0, (t.outline_level || 1) - 1);
                return (
                  <div
                    key={col.key}
                    style={{
                      width: col.width, padding: ci === 0 ? `0 8px 0 ${8 + indent * 12}px` : "0 8px",
                      display: "flex", alignItems: "center",
                      fontSize: 11, color: "#3F3F46",
                      fontFamily: (col.type === "number" || col.type === "percent") ? "ui-monospace,monospace" : "inherit",
                      justifyContent: (col.type === "number" || col.type === "percent") ? "flex-end" : "flex-start",
                      fontWeight: ci === 0 && t.is_summary ? 700 : 400,
                      overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis",
                    }}
                  >
                    {formatCell(val, col.type)}
                  </div>
                );
              })}
            </div>
            {/* Timeline bar */}
            {bounds && (
              <div style={{ width: bounds.timelineWidth, position: "relative" }}>
                {t._start && t._finish && (() => {
                  const left = diffDays(t._start, bounds.min) * bounds.colWidth;
                  const width = Math.max(2, (diffDays(t._finish, t._start) + 1) * bounds.colWidth);
                  const pct = t.percent_complete || 0;
                  const tint = t.__color || syncMeta[t.sync_id]?.color;
                  const bg = t.is_summary ? "#3F3F46" : t.is_critical ? "#F97316" : "#2563EB";
                  return (
                    <div style={{
                      position: "absolute", top: 6, bottom: 6, left, width,
                      borderRadius: 2, background: tint || bg,
                      display: "flex", alignItems: "center", padding: "0 6px",
                      fontSize: 9, color: "white", fontFamily: "ui-monospace,monospace",
                      overflow: "hidden", whiteSpace: "nowrap",
                    }}>
                      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${pct}%`, background: "rgba(0,0,0,0.3)", borderRadius: 2 }} />
                      <span style={{ position: "relative", zIndex: 1 }}>{t.task_id}</span>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        ))}
      </div>
      {tasks.length > MAX_EXPORT_ROWS && (
        <div style={{ padding: 8, fontSize: 11, color: "#A1A1AA", borderTop: "1px solid #E4E4E7" }}>
          Exibindo as primeiras {MAX_EXPORT_ROWS} linhas. Use filtros para reduzir o conjunto.
        </div>
      )}
      <div style={{ padding: 8, fontSize: 10, color: "#71717A", borderTop: "1px solid #E4E4E7", display: "flex", justifyContent: "space-between" }}>
        <span>Cornerstone · {rows.length} tarefas</span>
        <span>{new Date().toLocaleString()}</span>
      </div>
      <div style={{ minHeight: totalHeight }} />
    </div>
  );
}
